import { useState, useEffect } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import api from '@/lib/api';
import { toast } from 'sonner';
import { MessageSquare, Search, Mail, Phone, Building2 } from 'lucide-react';

const statusColors = {
  'New': 'bg-blue-50 text-blue-700',
  'Contacted': 'bg-amber-50 text-amber-700',
  'Converted': 'bg-emerald-50 text-emerald-700',
};

export default function AdminLeads() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const res = await api.get('/leads');
        setLeads(res.data);
      } catch (e) { console.error(e); }
      setLoading(false);
    };
    load();
  }, []);

  const updateStatus = async (leadId, newStatus) => {
    try {
      await api.put(`/leads/${leadId}/status`, { status: newStatus });
      setLeads(prev => prev.map(l => l.id === leadId ? { ...l, status: newStatus } : l));
      toast.success(`Lead status updated to ${newStatus}`);
    } catch (e) {
      toast.error('Failed to update status');
    }
  };

  const filtered = leads.filter(l =>
    l.name?.toLowerCase().includes(search.toLowerCase()) ||
    l.company?.toLowerCase().includes(search.toLowerCase()) ||
    l.email?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <DashboardLayout>
      <div data-testid="admin-leads-page" className="space-y-8">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-[#0A0A0A] tracking-tight">Leads</h1>
          <p className="text-sm text-[#52525B] mt-1">Manage inquiries from the Contact Us form</p>
        </div>

        <div className="relative max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A1A1AA]" />
          <Input
            placeholder="Search leads..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 h-10 rounded-md border-black/[0.08]"
            data-testid="leads-search-input"
          />
        </div>

        <div className="bg-white rounded-md border border-black/[0.06] overflow-hidden">
          {loading ? (
            <div className="flex justify-center py-20">
              <div className="w-6 h-6 border-2 border-[#1C1C1C] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-20">
              <MessageSquare className="w-10 h-10 text-[#A1A1AA] mx-auto mb-3" />
              <p className="text-sm text-[#A1A1AA]">No leads yet</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-b border-black/[0.04] hover:bg-transparent">
                  <TableHead className="text-[11px] font-semibold uppercase tracking-wide text-[#A1A1AA] h-11">Name</TableHead>
                  <TableHead className="text-[11px] font-semibold uppercase tracking-wide text-[#A1A1AA] h-11">Company</TableHead>
                  <TableHead className="text-[11px] font-semibold uppercase tracking-wide text-[#A1A1AA] h-11 hidden md:table-cell">Contact</TableHead>
                  <TableHead className="text-[11px] font-semibold uppercase tracking-wide text-[#A1A1AA] h-11 hidden lg:table-cell">Message</TableHead>
                  <TableHead className="text-[11px] font-semibold uppercase tracking-wide text-[#A1A1AA] h-11">Date</TableHead>
                  <TableHead className="text-[11px] font-semibold uppercase tracking-wide text-[#A1A1AA] h-11">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((lead) => (
                  <TableRow key={lead.id} className="table-row-hover border-b border-black/[0.03]" data-testid={`lead-row-${lead.id}`}>
                    <TableCell className="text-sm font-medium text-[#0A0A0A]">{lead.name}</TableCell>
                    <TableCell className="text-sm text-[#52525B]">
                      <div className="flex items-center gap-1.5">
                        <Building2 className="w-3 h-3 text-[#A1A1AA]" />
                        {lead.company || '-'}
                      </div>
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      <div className="space-y-1">
                        <div className="flex items-center gap-1.5 text-xs text-[#52525B]">
                          <Mail className="w-3 h-3 text-[#A1A1AA]" />
                          {lead.email}
                        </div>
                        {lead.phone && (
                          <div className="flex items-center gap-1.5 text-xs text-[#52525B]">
                            <Phone className="w-3 h-3 text-[#A1A1AA]" />
                            {lead.phone}
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-[#52525B] hidden lg:table-cell">
                      <p className="truncate max-w-[200px]">{lead.message}</p>
                    </TableCell>
                    <TableCell className="text-xs text-[#A1A1AA]">
                      {new Date(lead.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' })}
                    </TableCell>
                    <TableCell>
                      <Select value={lead.status || 'New'} onValueChange={(v) => updateStatus(lead.id, v)}>
                        <SelectTrigger className={`h-8 w-[120px] rounded-full text-xs font-semibold border-0 ${statusColors[lead.status] || statusColors['New']}`} data-testid={`lead-status-${lead.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="New">New</SelectItem>
                          <SelectItem value="Contacted">Contacted</SelectItem>
                          <SelectItem value="Converted">Converted</SelectItem>
                        </SelectContent>
                      </Select>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
